import { ReportHeader } from '../../header/report-header';
import React, { useEffect, useState } from 'react';
import { AssignmentsSideBarFilter } from '../../education/assignments/side-bar-filter/assignments-side-bar-filter';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { formatDate } from '../../../../utils/date-format';
import { ChecksSideBarFilter } from './side-bar/checks-side-bar-filter';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { getReportCheckOperation } from './operations/get-report-check-operation';
import { CheckType } from '../../../../api/types/reports/accountant/check.type';
import { ChecksSideBarFilterType } from './side-bar/checks-side-bar-filter.type';
import { CheckStatusType } from '../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';
import { UpdateStatusCheckDialog } from './update-status-check/update-status-check-dialog';

export const ReportChecks = () => {
  const [data, setData] = useState<CheckType[]>([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedCheck, setSelectedCheck] = useState<CheckType | null>(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);

  const [filters, setFilters] = useState<ChecksSideBarFilterType>({
    branchIds: [],
    checkRepaymentStartDate: null,
    checkRepaymentEndDate: null,
    paymentCollectionEndDate: null,
    paymentCollectionStartDate: null,
    checkStatuses: [],
    docNumber: null,
    checkReferenceId: null,
  });

  const fetchReportData = async (
    nextFilters: ChecksSideBarFilterType,
    newOffset: number = offset,
    newLimit: number = limit,
  ) => {
    setFilters(nextFilters);
    setOffset(newOffset);
    setLimit(newLimit);
    await getReportCheckOperation(
      nextFilters,
      newOffset,
      newLimit,
      setData,
      setTotal,
      setLoading,
    );
  };

  const handleOnSearch = async (searchFilters: ChecksSideBarFilterType) => {
    await fetchReportData(searchFilters, 0, limit);
  };

  const onPageChange = (event: DataTablePageEvent) => {
    fetchReportData(filters, event.first, event.rows);
  };

  const getStatusSeverity = (
    status: CheckStatusType,
  ): 'success' | 'warning' | 'danger' | 'info' => {
    switch (status) {
      case CheckStatusType.CLEARED:
        return 'success';
      case CheckStatusType.DEPOSITED:
        return 'info';
      case CheckStatusType.PENDING:
        return 'warning';
      case CheckStatusType.BOUNCED:
      case CheckStatusType.CANCELLED:
      case CheckStatusType.EXPIRED:
        return 'danger';
      default:
        return 'warning';
    }
  };

  const getStatusLabel = (status: CheckStatusType): string => {
    switch (status) {
      case CheckStatusType.PENDING:
        return 'ממתין להפקדה';
      case CheckStatusType.DEPOSITED:
        return 'הופקד';
      case CheckStatusType.CLEARED:
        return 'פורע';
      case CheckStatusType.BOUNCED:
        return 'חזר';
      case CheckStatusType.CANCELLED:
        return 'בוטל';
      case CheckStatusType.EXPIRED:
        return 'פג תוקף';
      default:
        return 'לא ידוע';
    }
  };

  const handleEditStatus = (check: CheckType) => {
    setSelectedCheck(check);
    setShowUpdateDialog(true);
  };

  const handleDialogClose = () => {
    setShowUpdateDialog(false);
    setSelectedCheck(null);
  };

  const handleStatusUpdated = () => {
    fetchReportData(filters, offset, limit);
    handleDialogClose();
  };

  useEffect(() => {
    fetchReportData(filters);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <ReportHeader
        title="דו״ח המחאות"
        description="דו״ח זה מציג מידע אודות כלל ההמחאות של האירגון. הדו״ח מאפשר מעקב אחר המחאות לפי הסטטוסים השונים."
        metadata={[
          { label: 'מטרת הדו״ח', value: 'מעקב אחר המחאות לפי הסטטוסים השונים' },
          { label: 'תדירות עדכון', value: 'יומי' },
        ]}
        category="accounting"
      />

      <div style={{ display: 'flex', gap: '20px' }}>
        <div
          style={{
            height: 'calc(100vh - 230px)',
            minHeight: '100%',
            width: '300px',
            minWidth: '250px',
          }}
        >
          <ChecksSideBarFilter onSearch={handleOnSearch} />
        </div>

        {/* Table Section */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <div
            style={{ fontSize: '14px', color: '#4b5563', textAlign: 'right' }}
          >
            סה״כ רשומות: {total.toLocaleString()}
          </div>

          <DataTable
            value={data}
            rowHover
            loading={loading}
            paginator
            rows={limit}
            first={offset}
            totalRecords={total}
            onPage={onPageChange}
            lazy={true}
            rowClassName={() => 'cursor-pointer'}
            rowsPerPageOptions={[10, 20, 50, 100]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            emptyMessage={
              <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
            }
            dataKey="id"
            scrollable
            scrollHeight="calc(100vh - 230px)"
            tableStyle={{
              direction: 'rtl',
            }}
            className="assignments-table"
          >
            <Column
              field="branchName"
              header="שלוחה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="paymentReceiptDate"
              header="תאריך קליטת תשלום"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => formatDate(rowData.paymentReceiptDate)}
            />
            <Column
              field="firstName"
              header="שם משלם"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => (
                <label>
                  {rowData.firstName} {rowData.lastName}
                </label>
              )}
            />
            <Column
              field="bankNumber"
              header="בנק"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="accountNumber"
              header="חשבון"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="branchNumber"
              header="סניף"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="checkNumber"
              header="מס המחאה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="checkAmount"
              header="סכום"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="checkPaymentDate"
              header="תאריך פירעון"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => formatDate(rowData.checkPaymentDate)}
            />
            <Column
              field="checkStatus"
              header="סטטוס המחאה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    icon="pi pi-pencil"
                    size="small"
                    outlined
                    severity="secondary"
                    onClick={() => handleEditStatus(rowData)}
                    tooltip="עריכת סטטוס"
                    tooltipOptions={{ position: 'top' }}
                  />
                  <Tag
                    style={{ minWidth: '100px' }}
                    value={getStatusLabel(rowData.checkStatus)}
                    severity={getStatusSeverity(rowData.checkStatus)}
                  />
                </div>
              )}
            />
            <Column
              header="מספר מסמך"
              style={{ textAlign: 'right', width: '140px' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData: CheckType) =>
                rowData.accountingDocument?.docNumber ?? (
                  <span style={{ color: '#9ca3af' }}>חסר</span>
                )
              }
            />
            <Column
              header="קובץ"
              style={{ textAlign: 'right', width: '100px' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData: CheckType) =>
                rowData.accountingDocument?.fileUrl ? (
                  <a
                    href={rowData.accountingDocument.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#2563eb',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <i className="pi pi-external-link" />
                    פתח
                  </a>
                ) : (
                  <span style={{ color: '#9ca3af' }}>חסר</span>
                )
              }
            />
          </DataTable>
        </div>
      </div>

      {showUpdateDialog && selectedCheck && (
        <UpdateStatusCheckDialog
          visible={showUpdateDialog}
          onHide={handleDialogClose}
          check={selectedCheck}
          onStatusUpdated={handleStatusUpdated}
        />
      )}
    </div>
  );
};
